import { useState, useEffect, useMemo } from 'react';
import { Table, Card, Typography, Space, Select, Statistic, Row, Col, Tag, Input, Empty, Progress } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import apiClient from '../api/index';
import { formatTimestamp } from '../utils/date';

const { Title, Text } = Typography;

interface CoverageRow {
  brand: string;
  line: string;
  products: number;
  with_data: number;
  stubs: number;
  no_answer: number;
  coverage: number;
  providers: Record<string, number>;
  last_asked: string | null;
}

interface CoverageReport {
  client: string;
  days: number;
  products: number;
  with_data: number;
  stubs: number;
  no_answer: number;
  coverage: number;
  providers: Record<string, number>;
  rows: CoverageRow[];
}

function ProviderTags({ providers }: { providers: Record<string, number> }) {
  const entries = Object.entries(providers);
  if (entries.length === 0) return <Text type="secondary">none</Text>;
  return (
    <Space size={4} wrap>
      {entries.map(([name, count]) => (
        <Tag key={name}>{name} {count}</Tag>
      ))}
    </Space>
  );
}

export function CoveragePage() {
  const [report, setReport] = useState<CoverageReport | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [days, setDays] = useState(60);
  const [filter, setFilter] = useState('');

  useEffect(() => {
    setLoading(true);
    setError(null);
    apiClient.get<CoverageReport>(`/api/v1/logs/coverage?days=${days}`)
      .then((res) => setReport(res.data))
      .catch((err) => setError(err?.response?.data?.message || 'Failed to load coverage'))
      .finally(() => setLoading(false));
  }, [days]);

  const rows = useMemo(() => {
    const query = filter.trim().toLowerCase();
    const all = report?.rows || [];
    return query ? all.filter((r) => `${r.brand} ${r.line}`.toLowerCase().includes(query)) : all;
  }, [report, filter]);

  const columns: ColumnsType<CoverageRow> = [
    {
      title: 'Brand', dataIndex: 'brand', key: 'brand',
      sorter: (a, b) => a.brand.localeCompare(b.brand),
      render: (v: string) => v || <Text type="secondary">(none)</Text>,
    },
    {
      title: 'Filed under', dataIndex: 'line', key: 'line',
      sorter: (a, b) => a.line.localeCompare(b.line),
      render: (v: string) => v || <Text type="secondary">-</Text>,
    },
    {
      title: 'Products', dataIndex: 'products', key: 'products', align: 'right',
      sorter: (a, b) => a.products - b.products,
    },
    {
      title: 'With data', dataIndex: 'with_data', key: 'with_data', align: 'right',
      sorter: (a, b) => a.with_data - b.with_data,
    },
    {
      title: 'Stubs', dataIndex: 'stubs', key: 'stubs', align: 'right',
      sorter: (a, b) => a.stubs - b.stubs, defaultSortOrder: 'descend',
    },
    {
      title: 'Coverage', dataIndex: 'coverage', key: 'coverage', width: 170,
      sorter: (a, b) => a.coverage - b.coverage,
      render: (v: number) => (
        <Progress percent={v} size="small" status={v < 25 ? 'exception' : 'normal'} />
      ),
    },
    {
      title: 'Providers', dataIndex: 'providers', key: 'providers',
      render: (p: Record<string, number>) => <ProviderTags providers={p} />,
    },
    {
      title: 'Last asked', dataIndex: 'last_asked', key: 'last_asked',
      render: (v: string | null) => (v ? formatTimestamp(v) : '-'),
    },
  ];

  if (error) {
    return (
      <div>
        <Title level={4}>Coverage</Title>
        <Empty description={error} />
      </div>
    );
  }

  return (
    <div>
      <Title level={4}>Coverage</Title>
      <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
        Products {report?.client || 'FN-Odoo'} asked PIM about, each counted once by its latest
        answer. A stub is a placeholder record with no provider data: the client holds nothing
        for that product.
      </Text>

      {report && (
        <Row gutter={16} style={{ marginBottom: 24 }}>
          <Col xs={12} sm={6}>
            <Card size="small"><Statistic title="Products" value={report.products} /></Card>
          </Col>
          <Col xs={12} sm={6}>
            <Card size="small"><Statistic title="With data" value={report.with_data} /></Card>
          </Col>
          <Col xs={12} sm={6}>
            <Card size="small"><Statistic title="Stubs" value={report.stubs} /></Card>
          </Col>
          <Col xs={12} sm={6}>
            <Card size="small">
              <Statistic title="Coverage" value={report.coverage} precision={1} suffix="%" />
            </Card>
          </Col>
          <Col span={24} style={{ marginTop: 12 }}>
            <Space>
              <Text type="secondary">Supplied by:</Text>
              <ProviderTags providers={report.providers} />
            </Space>
          </Col>
        </Row>
      )}

      <Space style={{ marginBottom: 16 }}>
        <Select
          value={days}
          onChange={setDays}
          options={[
            { value: 7, label: 'Last 7 days' },
            { value: 30, label: 'Last 30 days' },
            { value: 60, label: 'Last 60 days' },
            { value: 90, label: 'Last 90 days' },
          ]}
          style={{ width: 150 }}
        />
        <Input.Search
          placeholder="Filter by brand or line..."
          allowClear
          onSearch={setFilter}
          onChange={(e) => { if (!e.target.value) setFilter(''); }}
          style={{ width: 260 }}
        />
        <Text type="secondary">{rows.length} brand and line combinations</Text>
      </Space>

      <Table
        columns={columns}
        dataSource={rows}
        rowKey={(r) => `${r.brand}|${r.line}`}
        loading={loading}
        size="small"
        pagination={{ pageSize: 50, showSizeChanger: false }}
      />
    </div>
  );
}
